/**
 * Exit-intent modal for /app – "Before you go"
 * Shows once per session when cursor leaves viewport (desktop).
 * Captures email or sends to Advanced.
 */

import React, { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'exitIntentAppShown';

export const ExitIntentApp = ({ onClose, onTryAdvanced, onEmailCaptured }) => {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleMouseOut = useCallback((e) => {
    if (e.clientY > 10) return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    if ('ontouchstart' in window) return;
    sessionStorage.setItem(STORAGE_KEY, '1');
    setShow(true);
  }, []);

  useEffect(() => {
    document.addEventListener('mouseout', handleMouseOut);
    return () => document.removeEventListener('mouseout', handleMouseOut);
  }, [handleMouseOut]);

  const handleClose = () => {
    setShow(false);
    if (onClose) onClose();
  };

  const handleTryAdvanced = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'exit_intent_try_advanced', { event_category: 'conversion', event_label: 'App exit intent', value: 1 });
    }
    setShow(false);
    if (onTryAdvanced) onTryAdvanced();
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) return;
    setSubmitting(true);
    try {
      const res = await fetch('/.netlify/functions/capture-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          calculatorType: 'basic',
          source: 'app_exit_intent'
        })
      });
      if (res.ok) {
        setSubmitted(true);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'exit_intent_email_captured', { event_category: 'Email Capture', event_label: 'App exit intent', value: 1 });
        }
        if (onEmailCaptured) onEmailCaptured(email.trim());
      }
    } catch (err) {
      console.error('Exit intent email capture:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-2xl p-6 relative border-2 border-amber-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl leading-none"
          aria-label="Close"
        >
          ×
        </button>
        <div className="text-4xl mb-3">👋</div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Before you go</h3>
        <p className="text-gray-600 text-sm mb-4">
          Get your results by email, or try the <strong>Advanced</strong> calculator free (Monte Carlo, phased retirement, 98 years of data).
        </p>
        {!submitted ? (
          <form onSubmit={handleEmailSubmit} className="space-y-3 mb-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting || !email.trim()}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Email my results'}
            </button>
          </form>
        ) : (
          <p className="text-green-700 font-medium mb-4">✓ Check your email for your results.</p>
        )}
        <button
          type="button"
          onClick={handleTryAdvanced}
          className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg"
        >
          Try Advanced free →
        </button>
      </div>
    </div>
  );
};
